import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, MessageSquare, Archive } from "lucide-react";

const OPTIONS = [
  { key: "new", label: "New", color: "#F2A93B", icon: Circle },
  { key: "read", label: "Read", color: "#31517E", icon: CheckCircle2 },
  { key: "responded", label: "Responded", color: "#1F5C38", icon: MessageSquare },
  { key: "archived", label: "Archived", color: "#8b7a5e", icon: Archive },
];

/**
 * Small pill dropdown that lets an admin move a submission through its lifecycle.
 * kind ∈ { "contacts", "volunteers", "partners", "pledges" }
 */
export default function StatusControl({ kind, row, onChange }) {
  const [current, setCurrent] = useState(row.status || "new");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  async function setStatus(next) {
    if (next === current) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/${kind}/${row.id}`, { status: next });
      setCurrent(next);
      toast.success(`Marked as ${next}`);
      onChange?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status");
    } finally {
      setSaving(false);
      setOpen(false);
    }
  }

  const opt = OPTIONS.find((o) => o.key === current) || OPTIONS[0];
  const Icon = opt.icon;

  return (
    <div className="relative inline-block" data-testid={`status-control-${kind}-${row.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        data-testid={`status-toggle-${kind}-${row.id}`}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border-2 transition disabled:opacity-60"
        style={{ background: opt.color, borderColor: opt.color, color: "#fff" }}
      >
        <Icon size={12} />
        {saving ? "Saving…" : opt.label}
      </button>
      {open && (
        <div className="absolute z-20 mt-1 left-0 bg-white border border-ink/15 rounded-[10px] shadow-xl overflow-hidden min-w-[140px]" data-testid={`status-menu-${kind}-${row.id}`}>
          {OPTIONS.map((o) => {
            const OI = o.icon;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => setStatus(o.key)}
                data-testid={`status-set-${kind}-${row.id}-${o.key}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-ink/[.04] ${o.key === current ? "font-bold" : ""}`}
              >
                <OI size={13} style={{ color: o.color }} />
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
